import { ASSUNTO_POR_ID, ParametroDaLoja } from './catalogo-de-parametros'
import { CampoDeCadastro } from './secoes'

// Montado com fromCharCode: o intervalo dos acentos combinantes escrito como escape vira caractere solto no arquivo
const ACENTOS = new RegExp(`[${String.fromCharCode(0x300)}-${String.fromCharCode(0x36f)}]`, 'g')

export function normalizarParaBusca(texto: string | null | undefined): string {
	return (texto || '')
		.normalize('NFD')
		.replace(ACENTOS, '')
		.toLowerCase()
		.replace(/[-/_."']/g, ' ')
}

export function termosDaBusca(busca: string): string[] {
	return normalizarParaBusca(busca).split(/\s+/).filter(termo => termo.length > 1)
}

export function textoDoParametro(parametro: ParametroDaLoja): string {
	const assunto = ASSUNTO_POR_ID[parametro.assunto]
	return normalizarParaBusca([
		parametro.titulo,
		parametro.descricao,
		parametro.detalhe,
		parametro.busca.join(' '),
		parametro.antigo,
		assunto ? assunto.titulo : '',
		parametro.grupo,
		parametro.id,
		(parametro.opcoes || []).map(({ texto }) => texto).join(' '),
	].join(' | '))
}

/** Só o nome do campo e os sinônimos: com o nome da seção, "certificado" traria todos os campos de "Dados fiscais e certificado". */
export function textoDoCampo(campo: CampoDeCadastro): string {
	return normalizarParaBusca([campo.titulo, campo.busca.join(' ')].join(' | '))
}

/**
 * Decide uma vez por busca: se a frase inteira aparece em algum texto, vale a frase ("vale troca" não acha "não vale
 * em trocas"); senão, todas as palavras, em qualquer ordem.
 */
export function criarFiltroDaBusca(busca: string, todosOsTextos: string[]): (texto: string) => boolean {
	const termos = termosDaBusca(busca)
	if (!termos.length) return () => false
	const frase = termos.join(' ')
	const usaFrase = termos.length > 1 && todosOsTextos.some(texto => texto.includes(frase))
	return usaFrase
		? texto => texto.includes(frase)
		: texto => termos.every(termo => texto.includes(termo))
}

const escapar = (texto: string) =>
	texto.replace(/[&<>"]/g, caractere => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' } as Record<string, string>)[caractere])

/** HTML com os termos da busca marcados; o texto sai escapado, o único HTML é o <mark>. */
export function realcarBusca(texto: string, busca: string): string {
	const termos = termosDaBusca(busca)
	if (!termos.length || !texto) return escapar(texto || '')
	// Normaliza letra a letra, guardando de que posição do original veio cada letra normalizada
	let normalizado = ''
	const origem: number[] = []
	for (let i = 0; i < texto.length; i++) {
		const letra = normalizarParaBusca(texto[i])
		for (let k = 0; k < letra.length; k++) {
			normalizado += letra[k]
			origem.push(i)
		}
	}
	const marcado = new Array(texto.length).fill(false)
	termos.forEach(termo => {
		let inicio = normalizado.indexOf(termo)
		while (inicio !== -1) {
			for (let k = inicio; k < inicio + termo.length; k++) marcado[origem[k]] = true
			inicio = normalizado.indexOf(termo, inicio + termo.length)
		}
	})
	let html = ''
	let dentro = false
	for (let i = 0; i < texto.length; i++) {
		if (marcado[i] && !dentro) { html += '<mark>'; dentro = true }
		if (!marcado[i] && dentro) { html += '</mark>'; dentro = false }
		html += escapar(texto[i])
	}
	return dentro ? html + '</mark>' : html
}

/** Por que um resultado apareceu, quando não foi pelo título nem pela descrição: o nome antigo ou um sinônimo. */
export function motivoDoResultado(parametro: ParametroDaLoja, busca: string): { rotulo: string; texto: string } | null {
	const termos = termosDaBusca(busca)
	if (!termos.length) return null
	const visivel = normalizarParaBusca(`${parametro.titulo} ${parametro.descricao} ${parametro.detalhe || ''}`)
	if (termos.every(termo => visivel.includes(termo))) return null
	if (termos.some(termo => normalizarParaBusca(parametro.antigo).includes(termo))) {
		return { rotulo: 'Nome na tela antiga', texto: parametro.antigo }
	}
	const sinonimo = parametro.busca.find(palavra => termos.some(termo => normalizarParaBusca(palavra).includes(termo)))
	return sinonimo ? { rotulo: 'Também chamado de', texto: sinonimo } : null
}
